import type { GetQuickSearchResultsV2QueryVariables } from '../gql/generated/GetQuickSearchResultsV2';
import { useQuickSearchMachine } from '../machines/hooks/useQuickSearchMachine';
import { useQuickSearchListMode } from './useQuickSearchListMode';

type UseGetQuickSearchResultsProps = {
  requestVariables: GetQuickSearchResultsV2QueryVariables;
  skip: boolean;
  isSearchQueryStale: boolean;
  isInitial: boolean;
};

/**
 * Orchestrates the search functionality by combining lexical and NLP search queries.
 *
 * This hook coordinates multiple search operations and returns results in two distinct modes:
 * - **SINGLE mode**: Returns a single flat list of QuickSearchResults
 * - **MULTI mode**: Returns an array of QuickSearchResultSections, each containing a list of QuickSearchResults
 *
 * @param props - The configuration object for the search operation
 * @param props.requestVariables - GraphQL query variables for the search request
 * @param props.skip - Whether to skip the search query execution
 * @param props.isSearchQueryStale - Whether the search query is stale and should be deferred
 * @returns The search results in either single or multi mode format
 */
export const useGetQuickSearchResults = ({
  requestVariables,
  skip,
  isSearchQueryStale,
  isInitial,
}: UseGetQuickSearchResultsProps) => {
  const { listMode } = useQuickSearchListMode();

  return useQuickSearchMachine({
    requestVariables,
    listMode,
    skip,
    isSearchQueryStale,
    isInitial,
  });
};
